import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import * as React from 'react';
import { LabelMedium, LabelSmall } from '@khanacademy/wonder-blocks-typography';
import _extends from '@babel/runtime/helpers/extends';
import { StyleSheet } from 'aphrodite';
import Clickable from '@khanacademy/wonder-blocks-clickable';
import { View } from '@khanacademy/wonder-blocks-core';
import { Strut } from '@khanacademy/wonder-blocks-layout';
import { spacing, color, semanticColor, border } from '@khanacademy/wonder-blocks-tokens';

const CellMeasurements = {
  cellMinHeight: spacing.xxLarge_48,
  cellPadding: {
    paddingVertical: spacing.small_12,
    paddingHorizontal: spacing.medium_16
  },
  detailCellPadding: {
    paddingVertical: spacing.medium_16,
    paddingHorizontal: spacing.medium_16
  },
  accessoryHorizontalSpacing: spacing.medium_16
};
const getHorizontalRuleStyles = horizontalRule => {
  switch (horizontalRule) {
    case "inset":
      return [styles$2.horizontalRule, styles$2.horizontalRuleInset];
    case "full-width":
      return styles$2.horizontalRule;
    case "none":
      return {};
  }
};
const styles$2 = StyleSheet.create({
  horizontalRule: {
    position: "relative",
    ":after": {
      width: "100%",
      content: "''",
      position: "absolute",
      bottom: 0,
      right: 0,
      height: spacing.xxxxSmall_2,
      boxShadow: `inset 0px -1px 0px ${color.offBlack8}`
    }
  },
  horizontalRuleInset: {
    ":after": {
      width: `calc(100% - ${CellMeasurements.cellPadding.paddingHorizontal}px)`
    }
  }
});

const LeftAccessory = ({
  leftAccessory,
  leftAccessoryStyle,
  disabled
}) => {
  if (!leftAccessory) {
    return null;
  }
  return React.createElement(React.Fragment, null, React.createElement(View, {
    style: [styles$1.accessory, disabled && styles$1.accessoryDisabled, _extends({}, leftAccessoryStyle)]
  }, leftAccessory), React.createElement(Strut, {
    size: CellMeasurements.accessoryHorizontalSpacing
  }));
};
const RightAccessory = ({
  rightAccessory,
  rightAccessoryStyle,
  active,
  disabled
}) => {
  if (!rightAccessory) {
    return null;
  }
  return React.createElement(React.Fragment, null, React.createElement(Strut, {
    size: CellMeasurements.accessoryHorizontalSpacing
  }), React.createElement(View, {
    style: [styles$1.accessory, styles$1.accessoryRight, disabled && styles$1.accessoryDisabled, _extends({}, rightAccessoryStyle), active && styles$1.accessoryActive]
  }, rightAccessory));
};
function CellInner(props) {
  const {
    active,
    children,
    disabled,
    horizontalRule = "inset",
    contentStyle = undefined,
    leftAccessory = undefined,
    leftAccessoryStyle = undefined,
    rightAccessory = undefined,
    rightAccessoryStyle = undefined,
    style,
    testId,
    innerStyle
  } = props;
  const horizontalRuleStyles = getHorizontalRuleStyles(horizontalRule);
  return React.createElement(View, {
    style: [styles$1.innerWrapper, innerStyle, style, horizontalRuleStyles, active && styles$1.activeInnerWrapper],
    className: "inner-wrapper"
  }, React.createElement(LeftAccessory, {
    leftAccessory: leftAccessory,
    leftAccessoryStyle: leftAccessoryStyle,
    disabled: disabled
  }), React.createElement(View, {
    style: [styles$1.content, contentStyle],
    testId: testId
  }, children), React.createElement(RightAccessory, {
    rightAccessory: rightAccessory,
    rightAccessoryStyle: rightAccessoryStyle,
    active: active,
    disabled: disabled
  }));
}
const CellCore = props => {
  const {
    active,
    disabled,
    href,
    onClick,
    "aria-label": ariaLabel,
    "aria-selected": ariaSelected,
    "aria-checked": ariaChecked,
    target,
    role,
    rootStyle
  } = props;
  if (onClick || href) {
    return (React.createElement(Clickable, {
        disabled: disabled,
        onClick: onClick,
        href: href,
        hideDefaultFocusRing: true,
        "aria-label": ariaLabel ? ariaLabel : undefined,
        "aria-selected": ariaSelected ? ariaSelected : undefined,
        "aria-checked": ariaChecked,
        role: role,
        target: target,
        style: [styles$1.wrapper, styles$1.clickable, rootStyle, active && styles$1.active, disabled && styles$1.disabled],
        "aria-current": active ? "true" : undefined
      }, () => React.createElement(CellInner, props))
    );
  }
  return React.createElement(View, {
    style: [styles$1.wrapper, rootStyle, active && styles$1.active],
    "aria-current": active ? "true" : undefined,
    role: role
  }, React.createElement(CellInner, props));
};
const cellTokens = {
  root: {
    default: {
      background: semanticColor.surface.primary,
      foreground: semanticColor.text.primary
    },
    hover: {
      background: color.fadedBlue8
    },
    press: {
      background: color.fadedBlue8,
      border: semanticColor.surface.emphasis
    },
    selected: {
      background: color.fadedBlue8,
      foreground: color.activeBlue,
      border: semanticColor.surface.emphasis
    },
    focus: {
      border: semanticColor.focus.outer
    },
    disabled: {
      foreground: semanticColor.text.disabled,
      border: semanticColor.focus.outer
    }
  },
  accessory: {
    default: {
      foreground: semanticColor.icon.primary
    },
    selected: {
      foreground: semanticColor.icon.action
    },
    disabled: {
      foreground: semanticColor.icon.secondary
    }
  }
};
const styles$1 = StyleSheet.create({
  wrapper: {
    background: cellTokens.root.default.background,
    color: cellTokens.root.default.foreground,
    display: "flex",
    minHeight: CellMeasurements.cellMinHeight,
    textAlign: "left",
    width: "100%"
  },
  innerWrapper: {
    minHeight: CellMeasurements.cellMinHeight,
    padding: `${CellMeasurements.cellPadding.paddingVertical}px ${CellMeasurements.cellPadding.paddingHorizontal}px`,
    flexDirection: "row",
    flex: 1,
    borderRadius: "inherit",
    overflow: "hidden",
    height: "100%",
    ":focus-visible": {
      padding: `${CellMeasurements.cellPadding.paddingVertical - 2}px ${CellMeasurements.cellPadding.paddingHorizontal - 2}px`
    }
  },
  activeInnerWrapper: {
    position: "relative",
    ":before": {
      content: "''",
      position: "absolute",
      top: 0,
      left: 0,
      bottom: 0,
      width: border.width.thick,
      backgroundColor: cellTokens.root.selected.border
    }
  },
  content: {
    alignSelf: "center",
    flex: 1,
    overflowWrap: "break-word"
  },
  accessory: {
    minWidth: "auto",
    alignItems: "center",
    alignSelf: "center"
  },
  accessoryRight: {
    color: cellTokens.accessory.default.foreground
  },
  clickable: {
    outline: "none",
    [":hover[aria-disabled=true]"]: {
      cursor: "not-allowed"
    },
    ":focus-visible": {
      borderRadius: border.radius.radius_040,
      overflow: "hidden",
      position: "relative"
    },
    [":focus-visible:after"]: {
      content: "''",
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: 1,
      width: `calc(100% - ${spacing.xxxSmall_4}px)`,
      height: `calc(100% - ${spacing.xxxSmall_4}px)`,
      border: `${spacing.xxxxSmall_2}px solid ${cellTokens.root.focus.border}`,
      borderRadius: border.radius.radius_040
    },
    [":focus-visible[aria-disabled=true]:after"]: {
      borderColor: cellTokens.root.disabled.border
    },
    [":hover[aria-disabled=false]"]: {
      background: cellTokens.root.hover.background
    },
    [":active[aria-disabled=false]"]: {
      background: cellTokens.root.press.background
    },
    [":active[aria-disabled=false]:not([aria-current=true]) .inner-wrapper"]: {
      position: "relative",
      ":before": {
        content: "''",
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        width: border.width.medium,
        backgroundColor: semanticColor.surface.emphasis
      }
    }
  },
  active: {
    background: cellTokens.root.selected.background,
    color: cellTokens.root.selected.foreground,
    cursor: "default"
  },
  disabled: {
    color: cellTokens.root.disabled.foreground,
    ":focus-visible": {
      outline: "none"
    }
  },
  accessoryActive: {
    color: cellTokens.accessory.selected.foreground
  },
  accessoryDisabled: {
    color: cellTokens.accessory.disabled.foreground,
    opacity: 0.32
  }
});

const _excluded$1 = ["title"];
const CompactCell = function CompactCell(props) {
  const {
      title
    } = props,
    coreProps = _objectWithoutPropertiesLoose(props, _excluded$1);
  return React.createElement(CellCore, coreProps, typeof title === "string" ? React.createElement(LabelMedium, null, title) : title);
};

const _excluded = ["title", "subtitle1", "subtitle2"];
const Subtitle = ({
  subtitle,
  disabled
}) => {
  if (!subtitle) {
    return null;
  }
  if (typeof subtitle === "string") {
    return React.createElement(LabelSmall, {
      style: !disabled && styles.subtitle
    }, subtitle);
  }
  return subtitle;
};
const DetailCell = function DetailCell(props) {
  const {
      title,
      subtitle1,
      subtitle2
    } = props,
    coreProps = _objectWithoutPropertiesLoose(props, _excluded);
  return React.createElement(CellCore, _extends({}, coreProps, {
    innerStyle: styles.innerWrapper
  }), React.createElement(Subtitle, {
    subtitle: subtitle1,
    disabled: coreProps.disabled
  }), subtitle1 && React.createElement(Strut, {
    size: spacing.xxxxSmall_2
  }), typeof title === "string" ? React.createElement(LabelMedium, null, title) : title, subtitle2 && React.createElement(Strut, {
    size: spacing.xxxxSmall_2
  }), React.createElement(Subtitle, {
    subtitle: subtitle2,
    disabled: coreProps.disabled
  }));
};
const styles = StyleSheet.create({
  subtitle: {
    color: semanticColor.text.secondary
  },
  innerWrapper: {
    padding: `${CellMeasurements.detailCellPadding.paddingVertical}px ${CellMeasurements.detailCellPadding.paddingHorizontal}px`
  }
});

export { CompactCell, DetailCell };
